import { Printer, RotateCw, MonitorCheck, MonitorX } from "lucide-react";
import { toast } from "sonner";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, Loading, EmptyArt, Chip, cardCls } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { api, errMsg, fmtTime } from "@/lib/api";
import { JOB_STATUS } from "@/lib/mobile";
import { cn } from "@/lib/utils";

function Central({ central, now }) {
  const alive = central?.seen_at && new Date(now) - new Date(central.seen_at) < 20000;
  return (
    <div className={cn("flex items-start gap-3 rounded-2xl p-4", alive ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200" : "bg-red-50 text-red-900 ring-1 ring-red-200")} data-testid="m-central-status">
      {alive ? <MonitorCheck className="h-6 w-6 shrink-0" /> : <MonitorX className="h-6 w-6 shrink-0" />}
      <div className="text-sm">
        <p className="font-semibold">{alive ? `Computador do caixa conectado (${central.device})` : "Nenhum computador imprimindo agora"}</p>
        {alive ? <p className="text-xs opacity-80">Os pedidos enviados pelo celular saem na impressora dele.</p> : (
          <p className="text-xs opacity-80">No computador das impressoras, deixe o sistema completo aberto com o QZ Tray conectado. Em Configurações → Impressão, <b>"Imprimir pedidos do app de comandas neste computador"</b> precisa estar ligado.{central?.seen_at ? ` Último contato: ${fmtTime(central.seen_at)}.` : ""}</p>
        )}
      </div>
    </div>
  );
}

function Job({ j, onRetry }) {
  const [label, tone] = JOB_STATUS[j.status] || [j.status, "slate"];
  return (
    <li className={cn(cardCls, "flex items-start gap-3 p-3")} data-testid={`m-job-${j.id}`}>
      <Printer className="mt-0.5 h-5 w-5 shrink-0 text-[#1565c0]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{j.title || j.kind}</p>
        <p className="text-xs text-slate-500">{fmtTime(j.created_at)} · {j.created_by}{j.printed_at ? ` · impresso ${fmtTime(j.printed_at)}` : ""}</p>
        {j.error && <p className="text-xs text-red-600">{j.error}</p>}
      </div>
      <div className="flex flex-col items-end gap-1">
        <Chip tone={tone} testId={`m-job-status-${j.id}`}>{label}</Chip>
        {["failed", "expired", "done"].includes(j.status) && <button onClick={() => onRetry(j)} className="flex items-center gap-1 text-xs font-semibold text-[#1565c0]" data-testid={`m-job-retry-${j.id}`}><RotateCw className="h-3.5 w-3.5" />{j.status === "done" ? "Imprimir de novo" : "Tentar de novo"}</button>}
      </div>
    </li>
  );
}

export default function MPrints() {
  const { data, load } = usePoll(() => api.get("/print-jobs").then((r) => r.data), 5000);
  const retry = (j) => api.post(`/print-jobs/${j.id}/retry`).then(() => { toast.success("Enviado de novo para a fila"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <>
      <MTopBar title="Impressões centralizadas" />
      <MPage>
        {!data ? <Loading /> : (
          <>
            <Central central={data.central} now={data.now} />
            {!data.jobs.length ? <EmptyArt icon={Printer} title="Nenhuma impressão ainda" text="Pedidos, contas e comprovantes enviados pelo celular aparecem aqui." testId="m-jobs-empty" />
              : <ul className="space-y-2" data-testid="m-jobs-list">{data.jobs.map((j) => <Job key={j.id} j={j} onRetry={retry} />)}</ul>}
          </>
        )}
      </MPage>
    </>
  );
}
